import { UserRole } from '../../auth/auth.service';

/**
 * The client's copy of the server authorisation policy.
 *
 * **`ab_server/src/services/permissionMatrix.ts` is the source of truth.** This
 * table must match it entry for entry; the server is what actually enforces, and
 * this exists only so the UI does not offer a control the API will refuse, or
 * hide one it would allow.
 *
 * Duplicated deliberately: server and client are independent npm packages with
 * no shared library, exactly as `UserRole` itself is mirrored in
 * `auth/auth.service.ts`. The alternative was what stood here before - eleven
 * hand-written `role === X` expressions that had drifted from the server in both
 * directions at once. `PLATFORM_OWNER` was excluded from six capabilities the
 * server grants it, so the platform console hid its own controls; and `ADMIN`
 * was granted `canManageChannels`, which the server refuses, so an MC admin was
 * shown channel management and then got a 403.
 *
 * A table makes a divergence a one-line diff against the server file. Eleven
 * scattered expressions made it invisible.
 */
export enum Capability {
  MANAGE_SYSTEM = 'manage_system',
  MANAGE_COMPANIES = 'manage_companies',

  VIEW_TEMPLATES = 'view_templates',
  MANAGE_TEMPLATES = 'manage_templates',

  MANAGE_CONTACTS = 'manage_contacts',
  MANAGE_STAGES = 'manage_stages',

  MANAGE_USERS = 'manage_users',
  ACCESS_ADMIN_ROUTES = 'access_admin_routes',

  VIEW_CHANNELS = 'view_channels',
  MANAGE_CHANNELS = 'manage_channels',
  MANAGE_CHANNEL_STATUS = 'manage_channel_status'
}

const MATRIX: Readonly<Record<UserRole, ReadonlySet<Capability>>> = Object.freeze({
  [UserRole.PLATFORM_OWNER]: new Set(Object.values(Capability)),

  // An RC: runs its own company and provisions/oversees the MCs beneath it.
  [UserRole.OWNER]: new Set([
    Capability.MANAGE_SYSTEM,
    Capability.MANAGE_COMPANIES,
    Capability.VIEW_TEMPLATES,
    Capability.MANAGE_TEMPLATES,
    Capability.MANAGE_CONTACTS,
    Capability.MANAGE_STAGES,
    Capability.MANAGE_USERS,
    Capability.ACCESS_ADMIN_ROUTES,
    Capability.VIEW_CHANNELS,
    Capability.MANAGE_CHANNELS,
    Capability.MANAGE_CHANNEL_STATUS
  ]),

  // An MC administrator: runs their own workspace, but does not provision
  // companies or create channels - those sit with the RC or the platform.
  [UserRole.ADMIN]: new Set([
    Capability.VIEW_TEMPLATES,
    Capability.MANAGE_TEMPLATES,
    Capability.MANAGE_CONTACTS,
    Capability.MANAGE_STAGES,
    Capability.MANAGE_USERS,
    Capability.ACCESS_ADMIN_ROUTES,
    Capability.VIEW_CHANNELS,
    Capability.MANAGE_CHANNEL_STATUS
  ]),

  // A standard MC user: day-to-day candidate work.
  [UserRole.MEMBER]: new Set([
    Capability.VIEW_TEMPLATES,
    Capability.MANAGE_CONTACTS
  ])
});

/**
 * Does this role hold this capability?
 *
 * Two guards, and the second is not paranoia. MATRIX is an object literal, so
 * `Object.prototype` sits in its lookup chain: `MATRIX['constructor']` returns a
 * function, and `.has(...)` on it THROWS. A lookup that throws is a 500 where the
 * contract says deny, and it is the same defect that made `normaliseRole` return
 * a truthy non-role. An own-property check is the fix in both places.
 *
 * Fails closed on an unrecognised or absent role: callers frequently hold a role
 * that came from a token or from stored user data, and `normaliseRole` returns
 * null for anything it cannot interpret. Passing that through here must deny,
 * never default.
 */
export function can(role: UserRole | null | undefined, capability: Capability): boolean {
  if (!role) return false;
  if (!Object.prototype.hasOwnProperty.call(MATRIX, role)) return false;

  return MATRIX[role].has(capability);
}

/** Every capability a role holds. For building a UI, not for gating. */
export function capabilitiesFor(role: UserRole | null | undefined): Capability[] {
  if (!role) return [];
  if (!Object.prototype.hasOwnProperty.call(MATRIX, role)) return [];

  return [...MATRIX[role]];
}
